import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, absoluteAssetUrl } from "../lib/api";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import StatusBadge from "../components/StatusBadge";

const SECTION_LABELS = {
    metadata: "Metadata",
    purpose_scope: "Purpose & scope",
    responsibilities: "Responsibilities",
    references: "References",
    apparatus_materials: "Apparatus & materials",
    safety_precautions: "Safety precautions",
    procedure: "Procedure",
    calculations: "Calculations",
    result_interpretation: "Result interpretation",
    shutdown_procedure: "Shutdown",
    test_precautions: "Test precautions",
    notes: "Notes",
    uncertainties: "Uncertainties",
    unknown: "Other",
};

export default function DocumentDetail() {
    const { id } = useParams();
    const nav = useNavigate();
    const [detail, setDetail] = useState(null);
    const [sections, setSections] = useState([]);
    const [steps, setSteps] = useState([]);
    const [media, setMedia] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        try {
            const d = await api.get(`/documents/${id}`);
            setDetail(d.data);
            const vid = d.data.current_version?.id;
            if (vid) {
                const [s, st, m] = await Promise.all([
                    api.get(`/documents/${id}/versions/${vid}/sections`),
                    api.get(`/documents/${id}/versions/${vid}/steps`),
                    api.get(`/documents/${id}/versions/${vid}/media`),
                ]);
                setSections(s.data);
                setSteps(st.data);
                setMedia(m.data);
            } else {
                setSections([]);
                setSteps([]);
                setMedia([]);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Auto-refresh while parsing
    useEffect(() => {
        if (!detail?.document) return;
        const st = detail.document.status;
        if (st !== "UPLOADED" && st !== "PARSING") return;
        const t = setInterval(load, 2500);
        return () => clearInterval(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [detail]);

    const mediaById = useMemo(() => {
        const m = new Map();
        for (const x of media) m.set(x.id, x);
        return m;
    }, [media]);

    if (loading || !detail) {
        return (
            <div data-testid="document-detail-loading" className="py-24 text-center text-sm text-neutral-500">
                Loading…
            </div>
        );
    }

    const doc = detail.document;
    const metadata = sections.find((s) => s.kind === "metadata")?.structured_json ?? {};
    const nonMetaSections = sections.filter((s) => s.kind !== "metadata");

    return (
        <div data-testid="document-detail-page" className="space-y-8">
            <div>
                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => nav("/knowledge/documents")}
                    data-testid="back-to-documents"
                    className="mb-4 -ml-2 text-neutral-600 hover:text-neutral-900"
                >
                    <ArrowLeft size={14} className="mr-1" />
                    Documents
                </Button>
                <div className="flex items-start justify-between gap-6">
                    <div>
                        <div className="text-[11px] font-medium uppercase tracking-widest text-neutral-500">
                            {doc.type}
                            {doc.parsed_doc_number && (
                                <span className="ml-2 mono">· {doc.parsed_doc_number}</span>
                            )}
                            {doc.parsed_revision && (
                                <span className="ml-2 mono">· {doc.parsed_revision}</span>
                            )}
                        </div>
                        <h1 className="mt-2 text-3xl font-medium tracking-tight text-neutral-900">
                            {doc.parsed_title || doc.original_filename}
                        </h1>
                        {doc.description && (
                            <p className="mt-2 text-sm text-neutral-500">{doc.description}</p>
                        )}
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-neutral-600">
                            <StatusBadge status={doc.status} />
                            {doc.department && (
                                <span className="rounded-md bg-neutral-100 px-2 py-0.5">
                                    {doc.department}
                                </span>
                            )}
                            {detail.owner_name && (
                                <span className="rounded-md bg-neutral-100 px-2 py-0.5">
                                    Owner · {detail.owner_name}
                                </span>
                            )}
                            {(doc.tags || []).map((t) => (
                                <span
                                    key={t}
                                    className="rounded-md bg-neutral-100 px-2 py-0.5"
                                >
                                    #{t}
                                </span>
                            ))}
                        </div>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-right">
                        <Counter label="Sections" value={detail.counts.sections} testid="count-sections" />
                        <Counter label="Steps" value={detail.counts.steps} testid="count-steps" />
                        <Counter label="Images" value={detail.counts.media} testid="count-media" />
                    </div>
                </div>
                {doc.status === "FAILED" && doc.parse_error && (
                    <div
                        data-testid="parse-error"
                        className="mt-4 rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700"
                    >
                        Parse error: {doc.parse_error}
                    </div>
                )}
            </div>

            <Tabs defaultValue="metadata">
                <TabsList data-testid="detail-tabs" className="bg-transparent border-b border-neutral-200 rounded-none p-0 h-auto">
                    <TabsTrigger value="metadata" data-testid="tab-metadata" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2">
                        Metadata
                    </TabsTrigger>
                    <TabsTrigger value="sections" data-testid="tab-sections" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2">
                        Sections
                    </TabsTrigger>
                    <TabsTrigger value="procedure" data-testid="tab-procedure" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2">
                        Procedure
                    </TabsTrigger>
                    <TabsTrigger value="images" data-testid="tab-images" className="rounded-none border-b-2 border-transparent data-[state=active]:border-neutral-900 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-4 py-2">
                        Images
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="metadata" className="pt-6">
                    <MetadataView data={metadata} />
                </TabsContent>

                <TabsContent value="sections" className="pt-6">
                    <div className="space-y-4" data-testid="sections-list">
                        {nonMetaSections.length === 0 && (
                            <EmptyHint text="No sections parsed yet." />
                        )}
                        {nonMetaSections.map((s) => (
                            <div
                                key={s.id}
                                data-testid={`section-${s.kind}`}
                                className="rounded-lg border border-neutral-200 bg-white p-5"
                            >
                                <div className="flex items-baseline justify-between">
                                    <div className="text-sm font-semibold text-neutral-900">
                                        {SECTION_LABELS[s.kind] || s.kind}
                                    </div>
                                    <div className="mono text-[11px] text-neutral-400">
                                        #{s.order_index}
                                    </div>
                                </div>
                                {s.source_heading_path?.length > 0 && (
                                    <div className="mt-1 text-[11px] text-neutral-500">
                                        Heading: {s.source_heading_path.join(" › ")}
                                    </div>
                                )}
                                <pre className="mt-3 whitespace-pre-wrap font-sans text-sm text-neutral-700 leading-relaxed">
                                    {s.raw_text || <span className="text-neutral-400">— empty —</span>}
                                </pre>
                            </div>
                        ))}
                    </div>
                </TabsContent>

                <TabsContent value="procedure" className="pt-6">
                    <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white" data-testid="procedure-table">
                        <table className="w-full text-sm">
                            <thead className="border-b border-neutral-200 bg-neutral-50">
                                <tr>
                                    <th className="px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-neutral-500 w-24">Step</th>
                                    <th className="px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-neutral-500">Instruction</th>
                                    <th className="px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-neutral-500 w-56">Images</th>
                                    <th className="px-4 py-2 text-left text-[11px] font-medium uppercase tracking-wider text-neutral-500 w-24">Flags</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-neutral-100">
                                {steps.length === 0 && (
                                    <tr>
                                        <td colSpan={4} className="py-8 text-center text-neutral-500">
                                            No procedure steps parsed yet.
                                        </td>
                                    </tr>
                                )}
                                {steps.map((s) => (
                                    <tr key={s.id} data-testid={`step-row-${s.step_number}`}>
                                        <td className="px-4 py-3 align-top mono font-medium text-neutral-900">
                                            {s.step_number}
                                        </td>
                                        <td className="px-4 py-3 align-top text-neutral-700 leading-relaxed">
                                            {s.original_instruction}
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            <div className="flex flex-wrap gap-1.5">
                                                {(s.image_asset_ids || []).length === 0 && (
                                                    <span className="text-[11px] text-neutral-400">—</span>
                                                )}
                                                {(s.image_asset_ids || []).map((mid) => {
                                                    const m = mediaById.get(mid);
                                                    if (!m) return null;
                                                    return (
                                                        <img
                                                            key={mid}
                                                            src={absoluteAssetUrl(m.signed_url)}
                                                            alt=""
                                                            className="h-12 w-16 rounded border border-neutral-200 object-cover"
                                                        />
                                                    );
                                                })}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 align-top">
                                            {s.safety_related && (
                                                <span className="inline-flex items-center rounded-md bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                                                    Safety
                                                </span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </TabsContent>

                <TabsContent value="images" className="pt-6">
                    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4" data-testid="images-gallery">
                        {media.length === 0 && (
                            <div className="col-span-full">
                                <EmptyHint text="No images extracted." />
                            </div>
                        )}
                        {media.map((m) => (
                            <div
                                key={m.id}
                                data-testid={`media-tile-${m.id}`}
                                className="overflow-hidden rounded-lg border border-neutral-200 bg-white"
                            >
                                <div className="aspect-video overflow-hidden bg-neutral-50">
                                    {m.signed_url ? (
                                        <img
                                            src={absoluteAssetUrl(m.signed_url)}
                                            alt=""
                                            className="h-full w-full object-cover"
                                        />
                                    ) : (
                                        <div className="flex h-full w-full items-center justify-center text-neutral-300">
                                            <ImageOff size={22} />
                                        </div>
                                    )}
                                </div>
                                <div className="flex items-center justify-between p-3">
                                    <div className="text-[11px] text-neutral-500">#{m.order_index_in_doc}</div>
                                    {m.step_number ? (
                                        <span className="rounded-md bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                                            Step {m.step_number}
                                        </span>
                                    ) : (
                                        <span className="rounded-md bg-neutral-100 px-2 py-0.5 text-[11px] font-medium text-neutral-500">
                                            Unlinked
                                        </span>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
}

function Counter({ label, value, testid }) {
    return (
        <div
            data-testid={testid}
            className="rounded-md border border-neutral-200 bg-white px-3 py-2 min-w-[72px]"
        >
            <div className="text-2xl font-medium tracking-tight text-neutral-900">
                {value ?? 0}
            </div>
            <div className="text-[10px] font-medium uppercase tracking-widest text-neutral-500">
                {label}
            </div>
        </div>
    );
}

function EmptyHint({ text }) {
    return (
        <div className="rounded-lg border border-dashed border-neutral-200 bg-white p-8 text-center text-sm text-neutral-500">
            {text}
        </div>
    );
}

function MetadataView({ data }) {
    const entries = Object.entries(data || {});
    if (entries.length === 0) {
        return <EmptyHint text="No metadata extracted from header/footer." />;
    }
    return (
        <dl className="divide-y divide-neutral-100 rounded-lg border border-neutral-200 bg-white" data-testid="metadata-list">
            {entries.map(([k, v]) => (
                <div key={k} className="grid grid-cols-3 gap-4 px-5 py-3">
                    <dt className="text-[11px] font-medium uppercase tracking-wider text-neutral-500 col-span-1">
                        {k.replaceAll("_", " ")}
                    </dt>
                    <dd className="text-sm text-neutral-800 col-span-2 mono">
                        {String(v)}
                    </dd>
                </div>
            ))}
        </dl>
    );
}
